<template>
  <div class="app-content pt-3 p-md-3 p-lg-4">
    <div class="container-fluid">
      <!-- Header -->
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="app-page-title mb-0">Student Management</h1>
        <button class="btn btn-primary" @click="openCreateModal">
          <b-icon icon="plus-circle" class="mr-1"></b-icon> Add Student
        </button>
      </div>

      <!-- Search & Filters -->
      <div class="app-card shadow-sm mb-4">
        <div class="app-card-body p-4">
          <div class="row mb-3">
            <div class="col-md-5">
              <b-input-group>
                <b-form-input
                  v-model="search"
                  placeholder="Search by name, email, or matric number..."
                  @keyup.enter="loadStudents"
                ></b-form-input>
                <b-input-group-append>
                  <button class="btn btn-primary" @click="loadStudents" :disabled="loading">
                    <b-spinner small v-if="loading"></b-spinner>
                    <b-icon icon="search" v-else></b-icon>
                  </button>
                </b-input-group-append>
              </b-input-group>
            </div>
            <div class="col-md-3">
              <b-form-select v-model="statusFilter" @change="loadStudents">
                <option value="">All Statuses</option>
                <option value="CURRENT">Current</option>
                <option value="GRADUATED">Graduated</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="WITHDRAWN">Withdrawn</option>
              </b-form-select>
            </div>
            <div class="col-md-2">
              <b-form-select v-model="perPage" @change="loadStudents">
                <option :value="25">25 per page</option>
                <option :value="50">50 per page</option>
                <option :value="100">100 per page</option>
              </b-form-select>
            </div>
          </div>

          <!-- Students Table -->
          <b-table
            :items="students"
            :fields="fields"
            :busy="loading"
            responsive
            striped
            hover
            show-empty
          >
            <template #table-busy>
              <div class="text-center my-4">
                <b-spinner variant="primary"></b-spinner>
                <div class="mt-2 text-muted">Loading students...</div>
              </div>
            </template>
            <template #empty>
              <div class="text-center text-muted py-4">No students found. Try searching.</div>
            </template>
            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>
            <template #cell(full_name)="data">
              <div class="d-flex align-items-center">
                <img
                  v-if="data.item.picture"
                  :src="'https://pgr.run.edu.ng/storage/' + data.item.picture"
                  class="rounded-circle mr-2"
                  style="width:30px;height:30px;object-fit:cover;"
                  @error="$event.target.style.display='none'"
                />
                <div>
                  <strong>{{ [data.item.surname, data.item.first_name, data.item.last_name].filter(Boolean).join(' ') }}</strong>
                  <br><small class="text-muted">{{ data.item.email }}</small>
                </div>
              </div>
            </template>
            <template #cell(matric_collect)="data">
              {{ data.value || data.item.matric_no || 'N/A' }}
            </template>
            <template #cell(programme_name)="data">
              <span v-if="data.value">{{ data.value }}</span>
              <span v-else class="text-muted">N/A</span>
              <br v-if="data.item.department">
              <small class="text-muted" v-if="data.item.department">{{ data.item.department }}</small>
            </template>
            <template #cell(degree_status)="data">
              <b-badge :variant="statusVariant(data.value)">{{ data.value || 'N/A' }}</b-badge>
            </template>
            <template #cell(profile_update_lock)="data">
              <b-badge :variant="data.value === 'lock' ? 'danger' : 'success'">
                {{ data.value === 'lock' ? 'Locked' : 'Unlocked' }}
              </b-badge>
            </template>
            <template #cell(actions)="data">
              <button class="btn btn-sm btn-outline-primary mr-1" @click="viewStudent(data.item.id)" title="View">
                <b-icon icon="eye"></b-icon>
              </button>
              <button class="btn btn-sm btn-info mr-1" @click="editStudent(data.item.id)" title="Edit Profile">
                <b-icon icon="pencil"></b-icon>
              </button>
              <button class="btn btn-sm btn-outline-secondary mr-1" @click="openDegreeManager(data.item.id)" title="Manage Degrees & Semesters">
                <b-icon icon="journal-bookmark"></b-icon>
              </button>
              <button
                class="btn btn-sm"
                :class="data.item.profile_update_lock === 'lock' ? 'btn-success' : 'btn-warning'"
                @click="toggleLock(data.item)"
                :disabled="togglingId === data.item.id"
                :title="data.item.profile_update_lock === 'lock' ? 'Unlock' : 'Lock'"
              >
                <b-spinner small v-if="togglingId === data.item.id"></b-spinner>
                <b-icon :icon="data.item.profile_update_lock === 'lock' ? 'unlock' : 'lock'" v-else></b-icon>
              </button>
              <button class="btn btn-sm btn-danger ml-1" @click="confirmDelete(data.item)" :disabled="deletingId === data.item.id">
                <b-spinner small v-if="deletingId === data.item.id"></b-spinner>
                <b-icon icon="trash" v-else></b-icon>
              </button>
            </template>
          </b-table>

          <!-- Pagination -->
          <div class="d-flex justify-content-between align-items-center" v-if="totalRows > perPage">
            <small class="text-muted">
              Showing {{ (currentPage - 1) * perPage + 1 }}-{{ Math.min(currentPage * perPage, totalRows) }} of {{ totalRows }}
            </small>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalRows"
              :per-page="perPage"
              @change="onPageChange"
              size="sm"
            ></b-pagination>
          </div>
        </div>
      </div>

      <!-- ==================== VIEW STUDENT MODAL ==================== -->
      <b-modal v-model="showViewModal" title="Student Details" size="xl" ok-only ok-title="Close" scrollable>
        <div v-if="loadingDetail" class="text-center my-4">
          <b-spinner variant="primary"></b-spinner>
          <div class="mt-2 text-muted">Loading student details...</div>
        </div>
        <template v-else-if="viewData">
          <div class="row mb-4">
            <div class="col-md-3 text-center" v-if="viewData.picture_url">
              <img :src="viewData.picture_url" class="rounded-circle mb-2" style="width:120px;height:120px;object-fit:cover;" @error="$event.target.style.display='none'" />
            </div>
            <div :class="viewData.picture_url ? 'col-md-9' : 'col-12'">
              <h4>{{ [viewData.surname, viewData.first_name, viewData.last_name].filter(Boolean).join(' ') }}</h4>
              <p class="text-muted mb-1">{{ viewData.email }}</p>
              <p class="mb-1"><strong>Matric:</strong> {{ viewData.matric_collect || viewData.matric_no || 'N/A' }}</p>
              <p class="mb-0"><strong>Phone:</strong> {{ viewData.phone || 'N/A' }}</p>
            </div>
          </div>

          <b-tabs content-class="mt-3">
            <b-tab title="Personal Info" active>
              <div class="row">
                <div class="col-md-4 mb-2"><strong>Title:</strong> {{ viewData.title || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Gender:</strong> {{ viewData.gender || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Marital Status:</strong> {{ viewData.marital_status || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Date of Birth:</strong> {{ viewData.date_of_birth || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>State of Origin:</strong> {{ viewData.state_of_origin || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Nationality:</strong> {{ viewData.nationality || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Place of Birth:</strong> {{ viewData.place_of_birth || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>LGA:</strong> {{ viewData.place_of_birth_LG || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Blood Group:</strong> {{ viewData.blood_group || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Genotype:</strong> {{ viewData.genotype || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Health Status:</strong> {{ viewData.health_status || 'N/A' }}</div>
              </div>
            </b-tab>
            <b-tab title="Address & Next of Kin">
              <div class="row">
                <div class="col-md-6 mb-2"><strong>Contact Address:</strong> {{ viewData.contact_address || 'N/A' }}</div>
                <div class="col-md-6 mb-2"><strong>Residential Address:</strong> {{ viewData.residential_address || 'N/A' }}</div>
              </div>
              <hr>
              <h6>Next of Kin</h6>
              <div class="row">
                <div class="col-md-4 mb-2"><strong>Name:</strong> {{ viewData.next_of_kin_name || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Relationship:</strong> {{ viewData.next_of_kin_relationship || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Email:</strong> {{ viewData.next_of_kin_email || 'N/A' }}</div>
                <div class="col-md-4 mb-2"><strong>Phone:</strong> {{ viewData.next_of_kin_phone || 'N/A' }}</div>
                <div class="col-md-8 mb-2"><strong>Address:</strong> {{ viewData.next_of_kin_address || 'N/A' }}</div>
              </div>
            </b-tab>

            <!-- Degrees & Semesters Tab -->
            <b-tab title="Degrees & Semesters">
              <div class="d-flex justify-content-end mb-2">
                <button class="btn btn-sm btn-outline-primary" @click="showViewModal = false; openDegreeManager(viewData.id);">
                  <b-icon icon="gear" class="mr-1"></b-icon> Manage Degrees
                </button>
              </div>
              <div v-if="!viewData.degrees || viewData.degrees.length === 0" class="text-muted text-center py-3">
                No degree records found for this student.
              </div>
              <div v-for="deg in viewData.degrees" :key="deg.id" class="mb-3">
                <div class="card">
                  <div class="card-header d-flex justify-content-between align-items-center py-2">
                    <div>
                      <strong>{{ deg.programme_name || 'Unknown Programme' }}</strong>
                      <span class="text-muted ml-2">({{ deg.degree || 'N/A' }} - {{ deg.prog_code || deg.prog_code_ref || 'N/A' }})</span>
                      <b-badge :variant="statusVariant(deg.status)" class="ml-2">{{ deg.status }}</b-badge>
                    </div>
                    <small class="text-muted">{{ deg.grant_type || '' }}</small>
                  </div>
                  <div class="card-body py-2 px-3">
                    <div class="mb-1" v-if="deg.department || deg.college">
                      <small class="text-muted">{{ deg.department }} | {{ deg.college }}</small>
                    </div>
                    <div v-if="deg.semesters && deg.semesters.length > 0">
                      <strong class="small">Registered Semesters ({{ deg.semesters.length }}):</strong>
                      <div class="d-flex flex-wrap mt-1">
                        <b-badge
                          v-for="sem in deg.semesters"
                          :key="sem.id"
                          variant="outline-primary"
                          class="mr-1 mb-1 border px-2 py-1"
                        >
                          {{ sem.session_name }} - {{ sem.semester_name }}
                        </b-badge>
                      </div>
                    </div>
                    <div v-else class="text-muted small">No semesters registered.</div>
                  </div>
                </div>
              </div>
            </b-tab>
          </b-tabs>
        </template>
      </b-modal>

      <!-- ==================== CREATE/EDIT STUDENT MODAL ==================== -->
      <b-modal
        v-model="showFormModal"
        :title="isEditing ? 'Edit Student' : 'Create Student'"
        size="xl"
        @ok.prevent="saveStudent"
        :ok-disabled="saving"
        no-close-on-backdrop
        scrollable
      >
        <template #modal-ok>
          <b-spinner small v-if="saving" class="mr-1"></b-spinner>
          {{ saving ? 'Saving...' : 'Save' }}
        </template>

        <b-tabs content-class="mt-3" v-model="formTab">
          <!-- Personal Info Tab -->
          <b-tab title="Personal Info">
            <div class="row">
              <div class="col-md-4">
                <b-form-group label="Surname *">
                  <b-form-input v-model="form.surname" :disabled="saving" :state="formErrors.surname ? false : null"></b-form-input>
                  <b-form-invalid-feedback>{{ formErrors.surname }}</b-form-invalid-feedback>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="First Name *">
                  <b-form-input v-model="form.first_name" :disabled="saving" :state="formErrors.first_name ? false : null"></b-form-input>
                  <b-form-invalid-feedback>{{ formErrors.first_name }}</b-form-invalid-feedback>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Last Name">
                  <b-form-input v-model="form.last_name" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <b-form-group label="Email *">
                  <b-form-input type="email" v-model="form.email" :disabled="saving" :state="formErrors.email ? false : null"></b-form-input>
                  <b-form-invalid-feedback>{{ formErrors.email }}</b-form-invalid-feedback>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Phone">
                  <b-form-input v-model="form.phone" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Title">
                  <b-form-select v-model="form.title" :disabled="saving">
                    <option :value="null">-- Select --</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                  </b-form-select>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <b-form-group label="Gender">
                  <b-form-select v-model="form.gender" :disabled="saving">
                    <option :value="null">-- Select --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-3">
                <b-form-group label="Marital Status">
                  <b-form-select v-model="form.marital_status" :disabled="saving">
                    <option :value="null">-- Select --</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Divorced">Divorced</option>
                    <option value="Widowed">Widowed</option>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-3">
                <b-form-group label="Date of Birth">
                  <b-form-input type="date" v-model="form.date_of_birth" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-3">
                <b-form-group label="Nationality">
                  <b-form-input v-model="form.nationality" placeholder="e.g. Nigerian" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <b-form-group label="State of Origin">
                  <b-form-input v-model="form.state_of_origin" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Place of Birth">
                  <b-form-input v-model="form.place_of_birth" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="LGA">
                  <b-form-input v-model="form.place_of_birth_LG" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-md-3">
                <b-form-group label="Blood Group">
                  <b-form-select v-model="form.blood_group" :disabled="saving">
                    <option :value="null">-- Select --</option>
                    <option v-for="bg in ['A+','A-','B+','B-','AB+','AB-','O+','O-']" :key="bg" :value="bg">{{ bg }}</option>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-3">
                <b-form-group label="Genotype">
                  <b-form-select v-model="form.genotype" :disabled="saving">
                    <option :value="null">-- Select --</option>
                    <option v-for="g in ['AA','AS','AC','SS','SC']" :key="g" :value="g">{{ g }}</option>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Health Status">
                  <b-form-input v-model="form.health_status" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
            </div>
          </b-tab>

          <!-- Academic Tab -->
          <b-tab title="Academic Info">
            <div class="row">
              <div class="col-md-4">
                <b-form-group label="Matric No (Registration)">
                  <b-form-input v-model="form.matric_no" placeholder="e.g. RUN/REG/PG/ADM/..." :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Matric Collect (ID Card)">
                  <b-form-input v-model="form.matric_collect" placeholder="e.g. RUN/MCM/21/11352" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-2">
                <b-form-group label="Active">
                  <b-form-select v-model="form.is_active" :disabled="saving">
                    <option :value="1">Active</option>
                    <option :value="0">Inactive</option>
                  </b-form-select>
                </b-form-group>
              </div>
              <div class="col-md-2">
                <b-form-group label="Profile Lock">
                  <b-form-select v-model="form.profile_update_lock" :disabled="saving">
                    <option value="unlock">Unlocked</option>
                    <option value="lock">Locked</option>
                  </b-form-select>
                </b-form-group>
              </div>
            </div>
            <div class="alert alert-info small mt-2" v-if="!isEditing">
              After creating the student, you'll be able to assign degree programmes and register semesters.
            </div>
            <div class="alert alert-info small mt-2" v-else>
              Use the <strong>Manage Degrees</strong> button (book icon) in the table to assign programmes and register semesters.
            </div>
          </b-tab>

          <!-- Address & Next of Kin Tab -->
          <b-tab title="Address & Next of Kin">
            <div class="row">
              <div class="col-md-6">
                <b-form-group label="Contact Address">
                  <b-form-textarea v-model="form.contact_address" rows="2" :disabled="saving"></b-form-textarea>
                </b-form-group>
              </div>
              <div class="col-md-6">
                <b-form-group label="Residential Address">
                  <b-form-textarea v-model="form.residential_address" rows="2" :disabled="saving"></b-form-textarea>
                </b-form-group>
              </div>
            </div>
            <hr>
            <h6>Next of Kin</h6>
            <div class="row">
              <div class="col-md-4">
                <b-form-group label="Name">
                  <b-form-input v-model="form.next_of_kin_name" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Relationship">
                  <b-form-input v-model="form.next_of_kin_relationship" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-4">
                <b-form-group label="Email">
                  <b-form-input type="email" v-model="form.next_of_kin_email" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
            </div>
            <div class="row">
              <div class="col-md-4">
                <b-form-group label="Phone">
                  <b-form-input v-model="form.next_of_kin_phone" :disabled="saving"></b-form-input>
                </b-form-group>
              </div>
              <div class="col-md-8">
                <b-form-group label="Address">
                  <b-form-textarea v-model="form.next_of_kin_address" rows="2" :disabled="saving"></b-form-textarea>
                </b-form-group>
              </div>
            </div>
          </b-tab>
        </b-tabs>
      </b-modal>

      <!-- ==================== MANAGE DEGREES MODAL ==================== -->
      <b-modal v-model="showDegreeModal" title="Manage Degrees & Semesters" size="xl" ok-only ok-title="Close" scrollable no-close-on-backdrop>
        <div v-if="loadingDetail" class="text-center my-4">
          <b-spinner variant="primary"></b-spinner>
          <div class="mt-2 text-muted">Loading...</div>
        </div>
        <template v-else-if="viewData">
          <h5 class="mb-3">{{ [viewData.surname, viewData.first_name].filter(Boolean).join(' ') }} - Degree Programmes</h5>

          <!-- Add Degree Form -->
          <div class="card mb-3">
            <div class="card-header py-2"><strong>Add New Degree</strong></div>
            <div class="card-body py-3">
              <div class="row">
                <div class="col-md-4">
                  <b-form-group label="Programme *" class="mb-2">
                    <b-form-select v-model="degreeForm.prog_id" :disabled="savingDegree" size="sm">
                      <option :value="null">-- Select Programme --</option>
                      <option v-for="p in programmes" :key="p.id" :value="p.id">
                        {{ p.programme }} ({{ p.degree }} - {{ p.prog_type }})
                      </option>
                    </b-form-select>
                  </b-form-group>
                </div>
                <div class="col-md-2">
                  <b-form-group label="Degree *" class="mb-2">
                    <b-form-select v-model="degreeForm.degree" :disabled="savingDegree" size="sm">
                      <option :value="null">-- Select --</option>
                      <option v-for="d in ['PGD','MSC','MA','MED','MBA','MPHIL','PHD']" :key="d" :value="d">{{ d }}</option>
                    </b-form-select>
                  </b-form-group>
                </div>
                <div class="col-md-2">
                  <b-form-group label="Status *" class="mb-2">
                    <b-form-select v-model="degreeForm.status" :disabled="savingDegree" size="sm">
                      <option value="CURRENT">Current</option>
                      <option value="GRADUATED">Graduated</option>
                      <option value="SUSPENDED">Suspended</option>
                      <option value="WITHDRAWN">Withdrawn</option>
                    </b-form-select>
                  </b-form-group>
                </div>
                <div class="col-md-2">
                  <b-form-group label="Grant Type" class="mb-2">
                    <b-form-input v-model="degreeForm.grant_type" :disabled="savingDegree" size="sm" placeholder="e.g. OTHERS"></b-form-input>
                  </b-form-group>
                </div>
                <div class="col-md-2 d-flex align-items-end mb-2">
                  <button class="btn btn-primary btn-sm btn-block" @click="addDegree" :disabled="savingDegree || !degreeForm.prog_id || !degreeForm.degree">
                    <b-spinner small v-if="savingDegree"></b-spinner>
                    <span v-else>Add Degree</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Existing Degrees -->
          <div v-if="!viewData.degrees || viewData.degrees.length === 0" class="text-muted text-center py-3">
            No degree records yet.
          </div>
          <div v-for="deg in viewData.degrees" :key="deg.id" class="card mb-3">
            <div class="card-header py-2 d-flex justify-content-between align-items-center">
              <div>
                <strong>{{ deg.programme_name || 'Unknown Programme' }}</strong>
                <span class="text-muted ml-1">({{ deg.degree || 'N/A' }} - {{ deg.prog_code || deg.prog_code_ref || 'N/A' }})</span>
                <b-badge :variant="statusVariant(deg.status)" class="ml-2">{{ deg.status }}</b-badge>
                <span class="text-muted ml-2 small" v-if="deg.grant_type">{{ deg.grant_type }}</span>
              </div>
              <div>
                <button class="btn btn-sm btn-outline-info mr-1" @click="startEditDegree(deg)" title="Edit">
                  <b-icon icon="pencil"></b-icon>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteDegree(deg)" :disabled="deletingDegreeId === deg.id">
                  <b-spinner small v-if="deletingDegreeId === deg.id"></b-spinner>
                  <b-icon icon="trash" v-else></b-icon>
                </button>
              </div>
            </div>
            <div class="card-body py-2 px-3">
              <div v-if="deg.department || deg.college" class="mb-2">
                <small class="text-muted">{{ deg.department }} | {{ deg.college }}</small>
              </div>

              <!-- Semesters for this degree -->
              <div class="d-flex justify-content-between align-items-center mb-2">
                <strong class="small">Registered Semesters ({{ (deg.semesters || []).length }}):</strong>
                <button class="btn btn-outline-primary btn-sm" @click="openAddSemester(deg)" :disabled="savingCalendar">
                  <b-icon icon="plus" class="mr-1"></b-icon> Add Semester
                </button>
              </div>
              <div v-if="deg.semesters && deg.semesters.length > 0">
                <div class="d-flex flex-wrap">
                  <span v-for="sem in deg.semesters" :key="sem.id" class="badge border mr-1 mb-1 px-2 py-1 d-inline-flex align-items-center" style="font-size:0.85em;">
                    {{ sem.session_name }} - {{ sem.semester_name }}
                    <button class="btn btn-link btn-sm text-danger p-0 ml-1" @click="confirmDeleteCalendar(sem)" :disabled="deletingCalendarId === sem.id" style="line-height:1;">
                      <b-spinner small v-if="deletingCalendarId === sem.id" style="width:12px;height:12px;"></b-spinner>
                      <b-icon icon="x" v-else font-scale="0.9"></b-icon>
                    </button>
                  </span>
                </div>
              </div>
              <div v-else class="text-muted small">No semesters registered for this degree.</div>
            </div>
          </div>
        </template>
      </b-modal>

      <!-- ==================== EDIT DEGREE MODAL ==================== -->
      <b-modal v-model="showEditDegreeModal" title="Edit Degree" @ok.prevent="updateDegree" :ok-disabled="savingDegree" no-close-on-backdrop>
        <template #modal-ok>
          <b-spinner small v-if="savingDegree" class="mr-1"></b-spinner>
          {{ savingDegree ? 'Saving...' : 'Update' }}
        </template>
        <div class="row">
          <div class="col-md-12">
            <b-form-group label="Programme *">
              <b-form-select v-model="editDegreeForm.prog_id" :disabled="savingDegree">
                <option :value="null">-- Select Programme --</option>
                <option v-for="p in programmes" :key="p.id" :value="p.id">
                  {{ p.programme }} ({{ p.degree }} - {{ p.prog_type }})
                </option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Degree *">
              <b-form-select v-model="editDegreeForm.degree" :disabled="savingDegree">
                <option :value="null">-- Select --</option>
                <option v-for="d in ['PGD','MSC','MA','MED','MBA','MPHIL','PHD']" :key="d" :value="d">{{ d }}</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Status *">
              <b-form-select v-model="editDegreeForm.status" :disabled="savingDegree">
                <option value="CURRENT">Current</option>
                <option value="GRADUATED">Graduated</option>
                <option value="SUSPENDED">Suspended</option>
                <option value="WITHDRAWN">Withdrawn</option>
              </b-form-select>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Prog Code">
              <b-form-input v-model="editDegreeForm.prog_code" :disabled="savingDegree"></b-form-input>
            </b-form-group>
          </div>
          <div class="col-md-6">
            <b-form-group label="Grant Type">
              <b-form-input v-model="editDegreeForm.grant_type" :disabled="savingDegree"></b-form-input>
            </b-form-group>
          </div>
        </div>
      </b-modal>

      <!-- ==================== ADD SEMESTER MODAL ==================== -->
      <b-modal v-model="showSemesterModal" title="Add Semester Registration" @ok.prevent="addCalendar" :ok-disabled="savingCalendar || !calendarForm.setting_id" no-close-on-backdrop>
        <template #modal-ok>
          <b-spinner small v-if="savingCalendar" class="mr-1"></b-spinner>
          {{ savingCalendar ? 'Saving...' : 'Add Semester' }}
        </template>
        <p class="text-muted mb-3">
          Adding semester to: <strong>{{ calendarDegreeLabel }}</strong>
        </p>
        <b-form-group label="Session / Semester *">
          <b-form-select v-model="calendarForm.setting_id" :disabled="savingCalendar">
            <option :value="null">-- Select Session/Semester --</option>
            <option v-for="s in settings" :key="s.id" :value="s.id">
              {{ s.session_name }} - {{ s.semester_name }}
            </option>
          </b-form-select>
        </b-form-group>
      </b-modal>

    </div>
  </div>
</template>

<script>
export default {
  name: "StudentProfile",
  data() {
    return {
      students: [],
      programmes: [],
      settings: [],
      loading: false,
      saving: false,
      loadingDetail: false,
      savingDegree: false,
      savingCalendar: false,
      search: "",
      statusFilter: "",
      perPage: 25,
      currentPage: 1,
      totalRows: 0,
      togglingId: null,
      deletingId: null,
      deletingDegreeId: null,
      deletingCalendarId: null,
      showViewModal: false,
      showFormModal: false,
      showDegreeModal: false,
      showEditDegreeModal: false,
      showSemesterModal: false,
      isEditing: false,
      editId: null,
      viewData: null,
      formTab: 0,
      form: this.emptyForm(),
      formErrors: {},
      degreeForm: { prog_id: null, degree: null, status: "CURRENT", prog_code: "", grant_type: "" },
      editDegreeForm: { id: null, prog_id: null, degree: null, status: "CURRENT", prog_code: "", grant_type: "" },
      calendarForm: { student_degree_id: null, setting_id: null },
      calendarDegreeLabel: "",
      fields: [
        { key: "index", label: "#", class: "text-center", thStyle: { width: "45px" } },
        { key: "full_name", label: "Student", sortable: false },
        { key: "matric_collect", label: "Matric No", sortable: true },
        { key: "programme_name", label: "Programme", sortable: true },
        { key: "degree_status", label: "Status", class: "text-center" },
        { key: "profile_update_lock", label: "Lock", class: "text-center" },
        { key: "actions", label: "Actions", class: "text-center", thStyle: { width: "220px" } },
      ],
    };
  },
  created() {
    this.loadStudents();
    this.loadProgrammes();
    this.loadSettings();
  },
  methods: {
    emptyForm() {
      return {
        surname: "", first_name: "", last_name: "", email: "", phone: "",
        title: null, gender: null, marital_status: null, date_of_birth: "",
        state_of_origin: "", place_of_birth: "", place_of_birth_LG: "",
        nationality: "", health_status: "", blood_group: null, genotype: null,
        matric_no: "", matric_collect: "", is_active: 1,
        profile_update_lock: "unlock",
        contact_address: "", residential_address: "",
        next_of_kin_name: "", next_of_kin_relationship: "",
        next_of_kin_email: "", next_of_kin_phone: "", next_of_kin_address: "",
      };
    },
    loadStudents() {
      this.loading = true;
      axios.get("admin/students", {
        params: {
          search: this.search,
          status: this.statusFilter,
          per_page: this.perPage,
          page: this.currentPage,
        },
      })
        .then((res) => {
          if (res.data.msg === "success") {
            this.students = res.data.data.data;
            this.totalRows = res.data.data.total;
          }
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load students" }))
        .finally(() => (this.loading = false));
    },
    loadProgrammes() {
      axios.get("admin/programmes").then((res) => {
        if (res.data.msg === "success") this.programmes = res.data.data;
      }).catch(() => {});
    },
    loadSettings() {
      axios.get("admin/all-settings").then((res) => {
        if (res.data.msg === "success") this.settings = res.data.data;
      }).catch(() => {});
    },
    onPageChange(page) {
      this.currentPage = page;
      this.loadStudents();
    },
    statusVariant(status) {
      const map = { CURRENT: "success", GRADUATED: "primary", SUSPENDED: "warning", WITHDRAWN: "danger" };
      return map[status] || "secondary";
    },

    // ---- View Student ----
    viewStudent(id) {
      this.viewData = null;
      this.showViewModal = true;
      this.loadingDetail = true;
      axios.get("admin/students/" + id)
        .then((res) => {
          if (res.data.msg === "success") this.viewData = res.data.data;
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load student" }))
        .finally(() => (this.loadingDetail = false));
    },

    // ---- Create / Edit Student ----
    openCreateModal() {
      this.isEditing = false;
      this.editId = null;
      this.form = this.emptyForm();
      this.formErrors = {};
      this.formTab = 0;
      this.showFormModal = true;
    },
    editStudent(id) {
      this.isEditing = true;
      this.editId = id;
      this.formErrors = {};
      this.formTab = 0;
      this.showFormModal = true;
      this.saving = true;
      axios.get("admin/students/" + id)
        .then((res) => {
          if (res.data.msg === "success") {
            let s = res.data.data;
            this.form = {
              surname: s.surname || "", first_name: s.first_name || "",
              last_name: s.last_name || "", email: s.email || "", phone: s.phone || "",
              title: s.title || null, gender: s.gender || null,
              marital_status: s.marital_status || null,
              date_of_birth: s.date_of_birth || "",
              state_of_origin: s.state_of_origin || "",
              place_of_birth: s.place_of_birth || "",
              place_of_birth_LG: s.place_of_birth_LG || "",
              nationality: s.nationality || "",
              health_status: s.health_status || "",
              blood_group: s.blood_group || null,
              genotype: s.genotype || null,
              matric_no: s.matric_no || "", matric_collect: s.matric_collect || "",
              is_active: s.is_active ?? 1,
              profile_update_lock: s.profile_update_lock || "unlock",
              contact_address: s.contact_address || "",
              residential_address: s.residential_address || "",
              next_of_kin_name: s.next_of_kin_name || "",
              next_of_kin_relationship: s.next_of_kin_relationship || "",
              next_of_kin_email: s.next_of_kin_email || "",
              next_of_kin_phone: s.next_of_kin_phone || "",
              next_of_kin_address: s.next_of_kin_address || "",
            };
          }
        })
        .catch(() => {
          Toast.fire({ icon: "error", title: "Failed to load student for editing" });
          this.showFormModal = false;
        })
        .finally(() => (this.saving = false));
    },
    saveStudent() {
      this.formErrors = {};
      if (!this.form.surname || !this.form.surname.trim()) { this.formErrors.surname = "Required"; this.formTab = 0; return; }
      if (!this.form.first_name || !this.form.first_name.trim()) { this.formErrors.first_name = "Required"; this.formTab = 0; return; }
      if (!this.form.email || !this.form.email.trim()) { this.formErrors.email = "Required"; this.formTab = 0; return; }
      this.saving = true;
      let req;
      if (this.isEditing) {
        req = axios.put("admin/students/" + this.editId, this.form);
      } else {
        req = axios.post("admin/students", this.form);
      }
      req.then((res) => {
        if (res.data.msg === "success") {
          Toast.fire({ icon: "success", title: this.isEditing ? "Student updated" : "Student created" });
          this.showFormModal = false;
          this.loadStudents();
          if (!this.isEditing) {
            this.openDegreeManager(res.data.data.id);
          }
        } else {
          Toast.fire({ icon: "error", title: res.data.message || "Operation failed" });
        }
      })
        .catch((err) => {
          if (err.response && err.response.status === 422) {
            let data = err.response.data;
            if (data.errors) {
              let errs = {};
              Object.entries(data.errors).forEach(([k, v]) => (errs[k] = v.join(", ")));
              this.formErrors = errs;
              this.formTab = 0;
            } else {
              Toast.fire({ icon: "error", title: data.message || "Validation error" });
            }
          } else {
            Toast.fire({ icon: "error", title: "An error occurred" });
          }
        })
        .finally(() => (this.saving = false));
    },

    // ---- Toggle Lock ----
    toggleLock(item) {
      this.togglingId = item.id;
      axios.patch("admin/students/" + item.id + "/toggle-lock")
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Profile " + res.data.lock_status });
            this.loadStudents();
          }
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to toggle lock" }))
        .finally(() => (this.togglingId = null));
    },

    // ---- Delete Student ----
    confirmDelete(item) {
      let name = [item.surname, item.first_name].filter(Boolean).join(" ");
      Swal.fire({
        title: "Delete Student?",
        text: 'Are you sure you want to delete "' + name + '"? This will also remove their degree and calendar records.',
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingId = item.id;
          axios.delete("admin/students/" + item.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Student deleted" });
                this.loadStudents();
              } else {
                Toast.fire({ icon: "error", title: res.data.message || "Delete failed" });
              }
            })
            .catch((err) => {
              Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to delete student" });
            })
            .finally(() => (this.deletingId = null));
        }
      });
    },

    // ---- Degree Manager ----
    openDegreeManager(studentId) {
      this.viewData = null;
      this.showDegreeModal = true;
      this.loadingDetail = true;
      this.degreeForm = { prog_id: null, degree: null, status: "CURRENT", prog_code: "", grant_type: "" };
      axios.get("admin/students/" + studentId)
        .then((res) => {
          if (res.data.msg === "success") this.viewData = res.data.data;
        })
        .catch(() => Toast.fire({ icon: "error", title: "Failed to load student" }))
        .finally(() => (this.loadingDetail = false));
    },
    refreshDegreeData() {
      if (!this.viewData) return;
      axios.get("admin/students/" + this.viewData.id)
        .then((res) => {
          if (res.data.msg === "success") this.viewData = res.data.data;
        })
        .catch(() => {});
    },
    addDegree() {
      if (!this.degreeForm.prog_id || !this.degreeForm.degree) return;
      this.savingDegree = true;
      let prog = this.programmes.find(p => p.id === this.degreeForm.prog_id);
      let progCode = prog ? (prog.code || "") : "";
      axios.post("admin/students/" + this.viewData.id + "/degrees", {
        ...this.degreeForm,
        prog_code: progCode || this.degreeForm.prog_code,
      })
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Degree added" });
            this.degreeForm = { prog_id: null, degree: null, status: "CURRENT", prog_code: "", grant_type: "" };
            this.refreshDegreeData();
            this.loadStudents();
          }
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to add degree" });
        })
        .finally(() => (this.savingDegree = false));
    },
    startEditDegree(deg) {
      this.editDegreeForm = {
        id: deg.id,
        prog_id: deg.prog_id,
        degree: deg.degree,
        status: deg.status,
        prog_code: deg.prog_code || "",
        grant_type: deg.grant_type || "",
      };
      this.showEditDegreeModal = true;
    },
    updateDegree() {
      this.savingDegree = true;
      axios.put("admin/students/" + this.viewData.id + "/degrees/" + this.editDegreeForm.id, this.editDegreeForm)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Degree updated" });
            this.showEditDegreeModal = false;
            this.refreshDegreeData();
            this.loadStudents();
          }
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to update degree" });
        })
        .finally(() => (this.savingDegree = false));
    },
    confirmDeleteDegree(deg) {
      Swal.fire({
        title: "Delete Degree?",
        text: "This will also remove all semester registrations for this degree.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingDegreeId = deg.id;
          axios.delete("admin/students/" + this.viewData.id + "/degrees/" + deg.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Degree deleted" });
                this.refreshDegreeData();
                this.loadStudents();
              }
            })
            .catch((err) => {
              Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to delete degree" });
            })
            .finally(() => (this.deletingDegreeId = null));
        }
      });
    },

    // ---- Calendar / Semester ----
    openAddSemester(deg) {
      this.calendarForm = { student_degree_id: deg.id, setting_id: null };
      this.calendarDegreeLabel = (deg.programme_name || "Unknown") + " (" + (deg.degree || "") + ")";
      this.showSemesterModal = true;
    },
    addCalendar() {
      if (!this.calendarForm.setting_id) return;
      this.savingCalendar = true;
      axios.post("admin/students/" + this.viewData.id + "/calendar", this.calendarForm)
        .then((res) => {
          if (res.data.msg === "success") {
            Toast.fire({ icon: "success", title: "Semester registered" });
            this.showSemesterModal = false;
            this.refreshDegreeData();
          }
        })
        .catch((err) => {
          Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to add semester" });
        })
        .finally(() => (this.savingCalendar = false));
    },
    confirmDeleteCalendar(sem) {
      Swal.fire({
        title: "Remove Semester?",
        text: "Remove this semester registration?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, remove",
      }).then((result) => {
        if (result.isConfirmed) {
          this.deletingCalendarId = sem.id;
          axios.delete("admin/students/" + this.viewData.id + "/calendar/" + sem.id)
            .then((res) => {
              if (res.data.msg === "success") {
                Toast.fire({ icon: "success", title: "Semester removed" });
                this.refreshDegreeData();
              }
            })
            .catch((err) => {
              Toast.fire({ icon: "error", title: err.response?.data?.message || "Failed to remove semester" });
            })
            .finally(() => (this.deletingCalendarId = null));
        }
      });
    },
  },
};
</script>
